import type * as React from 'react'
import type { ToastVariant } from './Toast'

type ToastId = number | string
type ToastMessage = string | React.ReactNode

const DEFAULT_DURATION = 5000

type ToastAction = {
  label: string | React.ReactNode
  altText?: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}

type ToasterToast = {
  id: ToastId
  message?: ToastMessage
  description?: ToastMessage
  variant?: ToastVariant
  dismissible?: boolean
  duration?: number
  action?: ToastAction
  actions?: ToastAction[]
  onDismiss?: (id: ToastId) => void
}

type ToastOptions = Omit<ToasterToast, 'id' | 'variant' | 'message'> & {
  id?: number | string
}

type ToastEvent =
  | {
      type: 'dismiss'
      id: ToastId
    }
  | {
      type: 'push'
      toast: ToasterToast
    }
  | {
      type: 'dismissAll'
    }
  | {
      type: 'remove'
      id: ToastId
    }

type ToastStateSubscriber = (event: ToastEvent) => void

/* Used to generate toast ids */
let toastsCounter = 1

class Observer {
  subscribers: Array<ToastStateSubscriber>
  toasts: Array<ToasterToast>

  constructor() {
    this.subscribers = []
    this.toasts = []
  }

  subscribe = (subscriber: ToastStateSubscriber) => {
    this.subscribers.push(subscriber)

    return () => {
      const index = this.subscribers.indexOf(subscriber)
      this.subscribers.splice(index, 1)
    }
  }

  publishEvent = (event: ToastEvent) => {
    this.subscribers.forEach((subscriber) => subscriber(event))
  }

  addToast = (toast: ToasterToast) => {
    this.toasts = [...this.toasts, toast]
    this.publishEvent({ type: 'push', toast })
  }

  updateToast = (id: ToastId, newToast: ToasterToast) => {
    const existingToast = this.toasts.find((toast) => toast.id === id)

    if (!existingToast) {
      return
    }

    const updatedToast: ToasterToast = {
      ...existingToast,
      ...newToast,
    }

    this.toasts = this.toasts.map((item) =>
      item.id === id ? updatedToast : item,
    )

    this.publishEvent({ type: 'push', toast: updatedToast })
  }

  create = (
    data: ToastOptions & {
      message?: ToastMessage
      variant?: ToastVariant
    },
  ) => {
    const alreadyExists =
      !!data?.id && this.toasts.some((toast) => toast.id === data.id)
    const id = data?.id ?? toastsCounter++

    if (alreadyExists) {
      this.updateToast(id, { id, ...data })
      return id
    }

    const preparedToast = {
      ...data,
      dismissible: data.dismissible ?? true,
      duration: data.duration ?? DEFAULT_DURATION,
      variant: data.variant ?? 'neutral',
      id,
    }

    this.addToast(preparedToast)
    return id
  }

  dismiss = (id: ToastId) => {
    this.publishEvent({ type: 'dismiss', id })
    return id
  }

  dismissAll = () => {
    this.publishEvent({ type: 'dismissAll' })
  }

  remove = (id: ToastId) => {
    this.toasts = this.toasts.filter((toast) => toast.id !== id)
    this.publishEvent({ type: 'remove', id })
  }

  message = (message: ToastMessage, data?: ToastOptions) => {
    return this.create({ ...data, message })
  }

  error = (message: ToastMessage, data?: ToastOptions) => {
    return this.create({ ...data, message, variant: 'error' })
  }

  success = (message: ToastMessage, data?: ToastOptions) => {
    return this.create({ ...data, variant: 'success', message })
  }

  warning = (message: ToastMessage, data?: ToastOptions) => {
    return this.create({ ...data, message, variant: 'warning' })
  }
}

const ToastState = new Observer()

const toast = Object.assign(ToastState.message, {
  success: ToastState.success,
  error: ToastState.error,
  warning: ToastState.warning,
  dismiss: ToastState.dismiss,
  dismissAll: ToastState.dismissAll,
})

export { toast, ToastState }
export type {
  ToastAction,
  ToastOptions,
  ToasterToast,
  ToastStateSubscriber,
  ToastId,
  ToastMessage,
  ToastEvent,
}
